import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { IconChevronRightSmall } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { TaskFieldSection } from '../tile/TaskFieldPrimitives';
import type { AutomationAssessment } from './automation-assessment-model';
import { toPercent } from './automation-assessment-scoring';
import { automationAssessmentTranslationKeys as keys } from './automation-assessment-translations';

const CARD_CLASS = 'flex w-full gap-xl rounded-xl bg-surface-subtle px-xl py-xl text-left';

/** The score as it appears on the summary card and on the detail tile heading. */
export const AutomationScore: FunctionComponent<{ readonly fraction: number; readonly size?: 'l' | 'm' }> = ({
    fraction,
    size = 'm',
}) => {
    const translate = useTranslate();

    return (
        <span
            className={`shrink-0 self-start font-medium leading-none tabular-nums text-text-primary ${
                size === 'l' ? 'text-[2rem]' : 'text-h3'
            }`}
            data-testid="automation-assessment-score"
        >
            {translate(keys.percent, { value: toPercent(fraction) })}
        </span>
    );
};

export const AutomationAiNotice: FunctionComponent = () => {
    const translate = useTranslate();

    return <p className="m-0 text-body-xs leading-normal text-text-content-faint">{translate(keys.aiNotice)}</p>;
};

const AssessmentSkeleton: FunctionComponent = () => {
    const translate = useTranslate();

    return (
        <div aria-label={translate(keys.pending)} className={CARD_CLASS} role="status">
            <div className="flex min-w-0 flex-1 flex-col gap-s">
                <SkeletonBase className="h-3.5 w-full" />
                <SkeletonBase className="h-3.5 w-full" />
                <SkeletonBase className="h-3.5 w-3/5" />
            </div>
            <SkeletonBase className="h-8 w-16 shrink-0" />
        </div>
    );
};

export const AutomationAssessmentSection: FunctionComponent<{
    readonly assessment: AutomationAssessment | undefined;
    /** The backend has queued or started the assessment and the result is not there yet. */
    readonly isPending: boolean;
    readonly onOpenDetails: (() => void) | undefined;
}> = ({ assessment, isPending, onOpenDetails }) => {
    const translate = useTranslate();

    if (assessment === undefined && !isPending) {
        return null;
    }

    return (
        <TaskFieldSection title={translate(keys.title)}>
            <div className="flex flex-col gap-s" data-testid="automation-assessment">
                {assessment === undefined ? (
                    <AssessmentSkeleton />
                ) : (
                    <button
                        className={`${CARD_CLASS} m-0 cursor-pointer appearance-none border-none transition-colors duration-100 ease-in-out hover:bg-surface-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring`}
                        data-testid="automation-assessment-card"
                        disabled={onOpenDetails === undefined}
                        type="button"
                        onClick={onOpenDetails}
                    >
                        <p className="m-0 min-w-0 flex-1 text-xs leading-normal text-text-secondary">
                            {assessment.summary}
                        </p>
                        <span className="flex shrink-0 items-start gap-xs">
                            <AutomationScore fraction={assessment.taskScore} />
                            <IconChevronRightSmall className="mt-xxs text-text-content-faint" size={16} />
                        </span>
                        <span className="sr-only">{translate(keys.openDetails)}</span>
                    </button>
                )}
                {assessment === undefined ? null : <AutomationAiNotice />}
            </div>
        </TaskFieldSection>
    );
};

/**
 * Per-step share on a workflow card, right of the title line. Where the card opens the step's
 * assessment, the chevron signals it; a card that only selects the step leaves it out.
 */
export const WorkflowStepScore: FunctionComponent<{
    readonly fraction: number | undefined;
    readonly isPending: boolean;
    readonly showChevron?: boolean;
}> = ({ fraction, isPending, showChevron = true }) => {
    const translate = useTranslate();

    if (isPending) {
        return <SkeletonBase className="h-4 w-8 shrink-0" />;
    }

    if (fraction === undefined) {
        return null;
    }

    const percent = toPercent(fraction);

    return (
        <span
            className={`flex shrink-0 items-center gap-xxs self-start text-body-xs leading-normal tabular-nums ${
                percent < 5 ? 'text-text-content-faint' : 'text-text-secondary'
            }`}
            data-testid="automation-assessment-step-score"
        >
            {translate(keys.percent, { value: percent })}
            {showChevron ? <IconChevronRightSmall className="text-text-content-faint" size={14} /> : null}
        </span>
    );
};
